import React, { useState } from 'react';
import {
  useGetBlogPostBySlugQuery,
} from '@/api/client';
import type {
  BlogPostListItemSchema,
  CreateBlogPostRequestSchema,
  UpdateBlogPostRequestSchema,
} from '@/api/client';
import RichTextEditor from '@feature/blog/components/RichTextEditor';
import { useAdminContext } from '../contexts/AdminProvider';

interface Props {
  posts: BlogPostListItemSchema[];
  isLoading: boolean;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  editingId: number | null;
  onEdit: (id: number | null) => void;
  onCreate: (body: CreateBlogPostRequestSchema) => Promise<void>;
  onUpdate: (id: number, body: UpdateBlogPostRequestSchema) => Promise<void>;
  onDelete: (id: number) => Promise<void>;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function BlogPostForm({
  initial,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  initial?: { title: string; slug: string; content: string; excerpt: string; status: string };
  onSubmit: (data: { title: string; slug: string; content: string; excerpt: string; status: string }) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  const [content, setContent] = useState(initial?.content ?? '');
  const [excerpt, setExcerpt] = useState(initial?.excerpt ?? '');
  const [status, setStatus] = useState(initial?.status ?? 'draft');
  const [autoSlug, setAutoSlug] = useState(!initial);

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (autoSlug) {
      setSlug(slugify(value));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({ title, slug, content, excerpt, status });
  };

  return (
    <form onSubmit={handleSubmit} className="admin-card">
      <div className="admin-card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Title</label>
          <input
            className="admin-form-input"
            value={title}
            onChange={e => handleTitleChange(e.target.value)}
            required
          />
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Slug</label>
          <input
            className="admin-form-input admin-mono"
            value={slug}
            onChange={e => { setAutoSlug(false); setSlug(e.target.value); }}
            required
          />
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Excerpt</label>
          <textarea
            className="admin-form-input"
            value={excerpt}
            onChange={e => setExcerpt(e.target.value)}
            rows={2}
            maxLength={500}
            style={{ resize: 'vertical' }}
          />
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Status</label>
          <select
            className="admin-form-input"
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </div>
        <div className="admin-form-group" style={{ margin: 0 }}>
          <label className="admin-form-label">Content</label>
          <RichTextEditor content={content} onChange={setContent} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', paddingTop: 'var(--space-2)' }}>
          <button type="button" className="admin-btn admin-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="admin-btn admin-btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </form>
  );
}

function BlogPostEditor({
  slug,
  postId,
  onUpdate,
  onCancel,
  isUpdating,
}: {
  slug: string;
  postId: number;
  onUpdate: (id: number, body: UpdateBlogPostRequestSchema) => Promise<void>;
  onCancel: () => void;
  isUpdating: boolean;
}) {
  const { data: fullPost, isLoading } = useGetBlogPostBySlugQuery(slug);

  if (isLoading || !fullPost) {
    return <div className="admin-loading">Loading post...</div>;
  }

  return (
    <BlogPostForm
      initial={{
        title: fullPost.title,
        slug: fullPost.slug,
        content: fullPost.content,
        excerpt: fullPost.excerpt ?? '',
        status: fullPost.status,
      }}
      onSubmit={(data) => onUpdate(postId, data)}
      onCancel={onCancel}
      isSubmitting={isUpdating}
    />
  );
}

export function BlogView({
  posts,
  isLoading,
  isCreating,
  isUpdating,
  isDeleting,
  editingId,
  onEdit,
  onCreate,
  onUpdate,
  onDelete,
}: Props) {
  const [showCreate, setShowCreate] = useState(false);
  const { showConfirmation } = useAdminContext();

  if (isLoading) {
    return <div className="admin-loading">Loading blog posts...</div>;
  }

  if (showCreate) {
    return (
      <BlogPostForm
        onSubmit={async (data) => {
          await onCreate(data);
          setShowCreate(false);
        }}
        onCancel={() => setShowCreate(false)}
        isSubmitting={isCreating}
      />
    );
  }

  if (editingId !== null) {
    const post = posts.find(p => p.id === editingId);
    if (!post) return null;

    return (
      <BlogPostEditor
        slug={post.slug}
        postId={editingId}
        onUpdate={onUpdate}
        onCancel={() => onEdit(null)}
        isUpdating={isUpdating}
      />
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-4)' }}>
        <button className="admin-btn admin-btn-primary" onClick={() => setShowCreate(true)}>
          New Post
        </button>
      </div>

      {posts.length === 0 ? (
        <div className="admin-empty">
          <div className="admin-empty-title">No blog posts yet</div>
          <div className="admin-empty-description">Create your first post to get started.</div>
        </div>
      ) : (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post.id}>
                  <td>
                    <span className="admin-td-label">Title</span>
                    <span className="admin-td-value" style={{ fontWeight: 500, color: 'var(--admin-fg)' }}>
                      {post.title}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Slug</span>
                    <span className="admin-td-value admin-mono">{post.slug}</span>
                  </td>
                  <td>
                    <span className="admin-td-label">Status</span>
                    <span className={`admin-badge ${post.status === 'published' ? 'admin-badge-success' : 'admin-badge-neutral'}`}>
                      {post.status}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Date</span>
                    <span className="admin-td-value admin-mono">
                      {new Date(post.created_at).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <span className="admin-td-label">Actions</span>
                    <div className="admin-actions" style={{ flexDirection: 'row' }}>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-secondary"
                        onClick={() => onEdit(post.id)}
                      >
                        Edit
                      </button>
                      <button
                        className="admin-btn admin-btn-sm admin-btn-danger"
                        disabled={isDeleting}
                        onClick={() =>
                          showConfirmation(
                            `Delete "${post.title}"?`,
                            () => onDelete(post.id)
                          )
                        }
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
